import { useState } from "react";
import { AlertTriangle } from "lucide-react";

import { AssetsService, type AssetBaseResponse } from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface AssetDeleteDialogProps {
  asset: AssetBaseResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function AssetDeleteDialog({
  asset,
  onClose,
  onSuccess,
}: AssetDeleteDialogProps) {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState("");

  const { mutate, isLoading, error, reset } = useMutation<
    void,
    AssetBaseResponse
  >(async () => AssetsService.delete(asset!.id), {
    onSuccess: () => {
      setConfirmName("");
      onSuccess();
    },
  });

  if (!asset) return null;

  const matches = confirmName.trim() === asset.name;

  const handleClose = () => {
    reset();
    setConfirmName("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matches) return;
    await mutate();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("assets.deleteDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("assets.deleteDialog.descriptionBefore")}
          <strong className="text-ocean-900">{asset.name}</strong>
          {t("assets.deleteDialog.descriptionAfter")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <Alert variant="danger">
            <div className="flex items-start gap-2 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                {t("assets.deleteDialog.confirmInstruction")}{" "}
                <strong className="font-mono">{asset.name}</strong>
              </div>
            </div>
          </Alert>
          <FormField
            label={t("assets.deleteDialog.confirmLabel")}
            value={confirmName}
            onChange={setConfirmName}
            placeholder={asset.name}
            error={
              confirmName.length > 0 && !matches
                ? t("assets.deleteDialog.confirmError")
                : undefined
            }
            required
          />
          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("assets.deleteDialog.cancel")}
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={isLoading || !matches}
          >
            {isLoading
              ? t("assets.deleteDialog.submitLoading")
              : t("assets.deleteDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
